import {
	DEFAULT_STATE_ID,
	DEFAULT_STORE_ID,
	DEFAULT_UNIQUE_ID,
	DELIVERY_LOCATION_COOKIE_NAME,
	DELIVERY_MARKET_COOKIE_NAME,
	GEO_LOCATION_COOKIE_NAME,
} from 'shared/custom/constants/commonConstants';
import {
	DeliveryLocationCookieType,
	DeliveryMarketCookieType,
	GeoLocationCookieType,
} from 'shared/custom/types/commonTypes';

export const getCookie = (name: string): string => {
	if (typeof document === 'undefined') return '';
	const cookies = document.cookie
		.split('; ')
		.map((cookie) => cookie.trim().split('='))
		.find(([key]) => key === name);
	return cookies?.[1] ?? '';
};
export const parseExpTime = (exdays: number) => {
	const d = new Date();
	d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
	return d;
};
export const getFormattedCookie = (cname: string, cvalue: string, exdays: number): string => {
	const d = new Date();
	d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
	let expires = '';
	if (exdays !== 0) {
		expires = ';expires=' + d.toUTCString();
	}
	return `${cname}=${cvalue}${expires};path=/`;
};

export const setCookie = (cname: string, cvalue: string, exdays: number) => {
	document.cookie = getFormattedCookie(cname, cvalue, exdays);
};

export const getCookieEventId = () => {
	const randNum = Math.floor(Math.random() * 50000) + 1;
	const unixTime = Math.floor(Date.now() / 1000);
	const eventId = randNum.toString() + unixTime.toString();
	return eventId;
};
/**
 *
 * @param cookieValue
 * @returns
 *
 * cookieValue is sent when we use this method in the server side
 */
export const getStoreIdAndStateId = (cookieValue?: string) => {
	const geoLocationCookie = cookieValue ? cookieValue : getCookie(GEO_LOCATION_COOKIE_NAME);
	if (geoLocationCookie && geoLocationCookie !== 'defaultStore') {
		const geoLocationCookieFormatted: GeoLocationCookieType = JSON.parse(geoLocationCookie);
		return {
			hasPreviousAnswer: geoLocationCookieFormatted.hasPreviousAnswer || false,
			stLocId: geoLocationCookieFormatted.stLocId || DEFAULT_STORE_ID,
			stateId: geoLocationCookieFormatted.stateId || DEFAULT_STATE_ID,
			uniqueId: geoLocationCookieFormatted.uniqueId || DEFAULT_UNIQUE_ID,
		};
	}
	// We don't have to return de default storeId and stateId because when is empty the prompt must be triggered
	return {
		hasPreviousAnswer: false,
		stLocId: DEFAULT_STORE_ID,
		stateId: DEFAULT_STATE_ID,
		uniqueId: DEFAULT_UNIQUE_ID,
	};
};

export const buildStoreCookie = (
	geoDataParams: GeoLocationCookieType,
	hasPreviousAnswer: boolean,
	cookieTime: number
) => {
	let geoLocationCookie: GeoLocationCookieType & { hasPreviousAnswer: boolean } = {
		hasPreviousAnswer,
		stLocId: '',
		stateId: '',
		uniqueId: '',
	};

	if (geoDataParams.stLocId !== '') {
		geoLocationCookie = {
			hasPreviousAnswer,
			stLocId: geoDataParams.stLocId,
			stateId: geoDataParams.stateId,
			uniqueId: geoDataParams.uniqueId || '',
		};
	}
	const geoLocationCookieS = JSON.stringify(geoLocationCookie);
	setCookie(GEO_LOCATION_COOKIE_NAME, geoLocationCookieS, cookieTime);
};

export const buildDeliveryLocationCookie = (
	geoDataParams: DeliveryLocationCookieType,
	cookieTime: number
) => {
	let deliveryLocationCookie: DeliveryLocationCookieType = {
		addressId: '',
		zipCode: '',
		locality: '',
		state: '',
		setByCustomer: false,
	};

	if (geoDataParams.zipCode !== '') {
		deliveryLocationCookie = {
			addressId: geoDataParams?.addressId ?? '',
			zipCode: geoDataParams.zipCode,
			locality: geoDataParams.locality,
			state: geoDataParams.state,
			setByCustomer: geoDataParams.setByCustomer ?? false,
		};
	}
	const geoLocationCookieS = JSON.stringify(deliveryLocationCookie);
	setCookie(DELIVERY_LOCATION_COOKIE_NAME, geoLocationCookieS, cookieTime);
};

export const getDeliveryLocationCookie = (): DeliveryLocationCookieType | undefined => {
	const deliveryLocationCookie = getCookie(DELIVERY_LOCATION_COOKIE_NAME);
	if (!deliveryLocationCookie) return undefined;
	try {
		return JSON.parse(deliveryLocationCookie);
	} catch {
		return undefined;
	}
};

/**
 * `true` when the delivery location already comes from a customer action — a zip code typed in
 * the location panel, a saved address or an accepted geolocation — so it must not be reassigned.
 */
export const hasCustomerDeliveryLocation = (): boolean =>
	Boolean(getDeliveryLocationCookie()?.setByCustomer);

/**
 * Delivery location write coming from a manual store selection. It only applies while the
 * customer has no delivery location of their own: selecting a store never overwrites a zip code
 * that came from the zip code input, a saved address or the geolocation.
 */
export const buildStoreDeliveryLocationCookie = (
	geoDataParams: DeliveryLocationCookieType,
	cookieTime: number
) => {
	if (hasCustomerDeliveryLocation()) return;
	buildDeliveryLocationCookie({ ...geoDataParams, setByCustomer: false }, cookieTime);
};

/**
 * Writes the market of the customer-set delivery zip code. Kept apart from `deliveryLocationHDP`
 * and written with the same expiration, so the location cookie contract is unchanged.
 */
export const buildDeliveryMarketCookie = (
	deliveryMarket: DeliveryMarketCookieType,
	cookieTime: number
) => {
	setCookie(DELIVERY_MARKET_COOKIE_NAME, JSON.stringify(deliveryMarket), cookieTime);
};

export const getDeliveryMarketCookie = (): DeliveryMarketCookieType | undefined => {
	const deliveryMarketCookie = getCookie(DELIVERY_MARKET_COOKIE_NAME);
	if (!deliveryMarketCookie) return undefined;
	try {
		return JSON.parse(deliveryMarketCookie);
	} catch {
		return undefined;
	}
};
